import React, { useEffect } from 'react';
import { translate } from 'translations/i18n';
import { uniqBy } from 'lodash';
import { useFormikContext } from 'formik';
import styled from 'styled-components';
import { useInView } from 'react-intersection-observer';

import MediaErrorBillboard from './MediaErrorBillboard';
import MediaSkeletonLoader from './MediaSkeletonLoader';
import MediaItem from './MediaItem';
import {
  MultiselectPicker,
  useMultiselect,
} from 'components/shared/forms/MultiselectPicker';

import { idExtractor } from './utils';
import {
  type ShowcaseProjectMediaItem,
  type ShowcaseProjectFeatureImage,
  useGetShowcaseProjectMedia,
} from 'javascripts/data/v3/showcases';

import { type WizardView } from '../constants';
import type { ShowcaseWizardFormData } from './Root';
import type { ProjectDatabaseId } from 'data/v1/projects';
import ViewWrapper from './ViewWrapper';

const SelectorContainer = styled.div`
  padding: 0 0 var(--cc_size_spacing_xxl);
`;

type CoverPhotoProps = {
  projectDatabaseId: ProjectDatabaseId;
  setView: (view: WizardView) => void;
  onConfirm: () => void;
};

const CoverPhoto = ({
  setView,
  onConfirm,
  projectDatabaseId,
}: CoverPhotoProps) => {
  const [ref, bottomOfFeedInView] = useInView({
    threshold: 0,
    rootMargin: '0px 0px 300px 0px',
  });
  const { values, setValues } = useFormikContext<ShowcaseWizardFormData>();

  const { data, isLoading, isError, refetch, fetchNextPage, isFetching } =
    useGetShowcaseProjectMedia({
      projectDatabaseId,
      params: { shared: true, assetType: 'image' },
    });
  // TODO: In the future, move this merge logic into a to util helper function
  const visibleMedia =
    data?.pages?.map((page) => page?.data?.items).flat() || [];
  const formValues = new Set([...values.assets].map(idExtractor));
  const selectedAssets = visibleMedia.filter((media) =>
    formValues.has(media.id),
  );
  const mediaItems = uniqBy([...selectedAssets, ...values.assets], 'id');
  const multiselect = useMultiselect({
    selectableItems: mediaItems,
    maxSelectionCount: 1,
    idExtractor,
  });

  const handleOnConfirm = () => {
    const selectedItem =
      multiselect.getAllSelected()?.[0] as ShowcaseProjectMediaItem;
    const newFeatureImage: ShowcaseProjectFeatureImage = {
      id: selectedItem.id,
      assetPreviewLarge: selectedItem.thumbnailUrl,
    };
    setValues({
      ...values,
      featuredImage: newFeatureImage,
    });
    setView('MAIN');
  };

  useEffect(() => {
    if (bottomOfFeedInView && !isFetching) {
      fetchNextPage();
    }
  }, [bottomOfFeedInView, fetchNextPage, isFetching]);

  // Initialize selected item
  useEffect(() => {
    multiselect.selectMany([values.featuredImage.id!]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mediaItems.length]);

  if (isLoading) {
    return (
      <ViewWrapper
        onConfirm={onConfirm}
        footerVisible
        headerTitle={translate(
          'showcases.create.v2PublishWizard.coverPhoto.title',
        )}
        headerDescription={translate(
          'showcases.create.v2PublishWizard.coverPhoto.description',
        )}
      >
        <MediaSkeletonLoader />
      </ViewWrapper>
    );
  }

  if (isError) {
    return (
      <ViewWrapper
        onConfirm={onConfirm}
        footerVisible={false}
        headerTitle={translate(
          'showcases.create.v2PublishWizard.coverPhoto.title',
        )}
        headerDescription={translate(
          'showcases.create.v2PublishWizard.coverPhoto.description',
        )}
      >
        <MediaErrorBillboard refetchAction={refetch} />
      </ViewWrapper>
    );
  }

  const selectedItem = multiselect.getAllSelected()?.[0];

  return (
    <ViewWrapper
      onConfirm={onConfirm}
      handleConfirmClick={handleOnConfirm}
      isConfirmDisabled={!selectedItem}
      handleCancelClick={() => setView('MAIN')}
      isCancelDisabled={false}
      headerTitle={translate(
        'showcases.create.v2PublishWizard.coverPhoto.title',
      )}
      headerDescription={translate(
        'showcases.create.v2PublishWizard.coverPhoto.description',
      )}
    >
      <SelectorContainer>
        <MultiselectPicker
          keyExtractor={(item) => `${item.type}-${item.id}`}
          multiselect={multiselect}
          selectableItems={mediaItems}
          ItemComponent={MediaItem}
        />
        <div ref={ref} style={{ height: '1px', opacity: 0 }} />
      </SelectorContainer>
    </ViewWrapper>
  );
};

export default CoverPhoto;
